import { buildLorebookSections } from './lorebookRuntime'
import { buildMessageContentWithDirectorNote } from './directorNotes'
import { renderConversationMacros } from './promptMacros'
import { compareTimelineEntries } from '@/lib/messageSlots'
import {
  estimateContentTokens,
  estimateTokens,
} from '@/services/conversation/context/composeContext'
import type {
  ContextRecipe,
  ContextSource,
  HistoryUnit,
} from '@/services/conversation/context/types'
import type { Bot, Character, Persona } from '@/types/domain'
import type { StoreMessage } from '@/lib/messageSlots'

export interface RoleplayContextOptions {
  isReroll?: boolean
  isResend?: boolean
  excludeMessageId?: number | string | null
  sourceMessageId?: number | string | null
}

/** Everything the roleplay sources are allowed to read. */
export interface RoleplayContextInput {
  userMessage: string
  character: Character
  persona: Persona
  messages: StoreMessage[]
  currentBot: Bot
  options: RoleplayContextOptions
}

/**
 * Section order inside the roleplay system message. Prompts are
 * whitespace-sensitive and order-sensitive, so both are declared rather than
 * implied by where a source happens to sit in a list.
 */
const SECTION = {
  characterCore: 10,
  scenario: 20,
  scenarioLore: 30,
  dialogueExamples: 40,
  lore: 50,
  persona: 60,
} as const

/** Dialogue examples stop being useful once the conversation has its own voice. */
const DIALOGUE_EXAMPLE_MESSAGE_LIMIT = 5

/** Headroom left for the reply itself. */
const RESPONSE_RESERVE_TOKENS = 100

const sortedHistory = (messages: StoreMessage[]): StoreMessage[] =>
  [...messages].sort(compareTimelineEntries)

/** History minus whatever this turn is replacing, and minus failed turns. */
function historyMessages(input: RoleplayContextInput): StoreMessage[] {
  const { messages, options } = input
  // A failed generation leaves an in-memory notice in the timeline. It is a
  // message to the reader, not part of the story, so it never reaches the model.
  const safeMessages = (Array.isArray(messages) ? messages : []).filter(
    (message) => message.phase !== 'error',
  )
  const { isReroll = false, isResend = false, excludeMessageId = null } = options

  if ((isReroll || isResend) && excludeMessageId != null) {
    return safeMessages.filter((message) => message.id !== excludeMessageId)
  }
  return safeMessages
}

/** Lorebook selection is shared by two system sections, so resolve it once. */
function lorebookSections(input: RoleplayContextInput) {
  const { character, messages, userMessage, currentBot } = input
  return buildLorebookSections({
    lorebooks: character.lorebooks,
    lorebook: character.lorebook,
    messages: Array.isArray(messages) ? [...messages] : [],
    userMessage,
    contextSize: currentBot.context_size || 4096,
    estimateTokens,
  })
}

const characterCoreSource: ContextSource<RoleplayContextInput> = {
  id: 'roleplay.character-core',
  contribute: ({ character, persona }) => ({
    systemFragments: [{
      id: 'roleplay.character-core',
      order: SECTION.characterCore,
      text: `You are ${character.character_name}. Here's your character information:\nPersonality: ${renderConversationMacros(character.personality, { persona })}`,
    }],
  }),
}

const scenarioSource: ContextSource<RoleplayContextInput> = {
  id: 'roleplay.scenario',
  contribute: ({ character, persona }) => {
    const scenario = renderConversationMacros(character.scenario, { persona })
    if (!scenario) return {}
    return {
      systemFragments: [{
        id: 'roleplay.scenario',
        order: SECTION.scenario,
        text: `Scenario: ${scenario}`,
      }],
    }
  },
}

const scenarioLoreSource: ContextSource<RoleplayContextInput> = {
  id: 'roleplay.scenario-lore',
  contribute: (input) => {
    const { scenarioText } = lorebookSections(input)
    if (!scenarioText) return {}
    return {
      systemFragments: [{
        id: 'roleplay.scenario-lore',
        order: SECTION.scenarioLore,
        text: `Relevant Scenario Lore:\n${scenarioText}`,
      }],
    }
  },
}

const dialogueExamplesSource: ContextSource<RoleplayContextInput> = {
  id: 'roleplay.dialogue-examples',
  contribute: ({ character, persona, messages }) => {
    const examples = renderConversationMacros(character.dialogue_examples, { persona })
    const botMessageCount = (Array.isArray(messages) ? messages : [])
      .filter((message) => !message.is_user_author).length
    if (!examples || botMessageCount >= DIALOGUE_EXAMPLE_MESSAGE_LIMIT) return {}
    return {
      systemFragments: [{
        id: 'roleplay.dialogue-examples',
        order: SECTION.dialogueExamples,
        text: `Dialogue Examples: ${examples}`,
      }],
    }
  },
}

const loreSource: ContextSource<RoleplayContextInput> = {
  id: 'roleplay.lore',
  contribute: (input) => {
    const { defaultText } = lorebookSections(input)
    if (!defaultText) return {}
    return {
      systemFragments: [{
        id: 'roleplay.lore',
        order: SECTION.lore,
        text: `Relevant Lore:\n${defaultText}`,
      }],
    }
  },
}

const personaSource: ContextSource<RoleplayContextInput> = {
  id: 'roleplay.persona',
  contribute: ({ persona }) => {
    const label = persona.pronouns
      ? `${persona.persona_name} (${persona.pronouns})`
      : persona.persona_name
    return {
      systemFragments: [{
        id: 'roleplay.persona',
        order: SECTION.persona,
        separator: '\n\n',
        text: `You are talking to ${label}: ${persona.description}`,
      }],
    }
  },
}

/**
 * The transcript. User turns carry their director note inline, so the model
 * sees the note exactly where the user aimed it.
 */
const historySource: ContextSource<RoleplayContextInput> = {
  id: 'roleplay.history',
  contribute: (input) => {
    const sorted = sortedHistory(historyMessages(input))
    const historyUnits: HistoryUnit[] = sorted.map((message, index) => {
      const role: 'user' | 'assistant' = message.is_user_author ? 'user' : 'assistant'
      const content = message.is_user_author
        ? buildMessageContentWithDirectorNote({ message, messages: sorted })
        : message.content
      return {
        id: message.id,
        order: index,
        messages: [{ role, content }],
        tokens: estimateContentTokens(content),
      }
    })
    return { historyUnits }
  },
}

/**
 * A resend re-states the user's message when history no longer contains it —
 * otherwise the model would be asked to reply to nothing.
 */
const resendSource: ContextSource<RoleplayContextInput> = {
  id: 'roleplay.resend',
  contribute: (input) => {
    const { options, userMessage, messages } = input
    if (!options.isResend || !userMessage) return {}

    const sorted = sortedHistory(historyMessages(input))
    const alreadyPresent = sorted.some((message) => {
      if (!message.is_user_author) return false
      const content = buildMessageContentWithDirectorNote({ message, messages: sorted })
      return typeof content === 'string' && content.includes(userMessage)
    })
    if (alreadyPresent) return {}

    const safeMessages = Array.isArray(messages) ? [...messages] : []
    const sourceMessage = options.sourceMessageId != null
      ? safeMessages.find((message) => String(message.id) === String(options.sourceMessageId)) || null
      : null

    return {
      trailingMessages: [{
        id: 'roleplay.resend',
        order: 10,
        message: {
          role: 'user',
          content: buildMessageContentWithDirectorNote({
            message: sourceMessage || { content: userMessage },
            messages: safeMessages,
            contentOverride: userMessage,
          }),
        },
      }],
    }
  },
}

/**
 * The recipe for a primary roleplay generation: the character performs for the
 * persona, with world knowledge pulled in by lorebook triggers.
 */
export const roleplayPrimaryRecipe: ContextRecipe<RoleplayContextInput> = {
  id: 'roleplay.primary',
  advancedPrompt: ({ currentBot, persona }) => ({ text: renderConversationMacros(currentBot.advanced_prompt, { persona }) }),
  sources: [
    characterCoreSource,
    scenarioSource,
    scenarioLoreSource,
    dialogueExamplesSource,
    loreSource,
    personaSource,
    historySource,
    resendSource,
  ],
  historyBudget: ({ userMessage, currentBot }, { systemText }) => (
    (currentBot.context_size || 4096)
    - estimateTokens(systemText)
    - estimateTokens(userMessage)
    - RESPONSE_RESERVE_TOKENS
  ),
}

export default roleplayPrimaryRecipe
